import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, Send, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { apiError } from "@/lib/platform";
import { Pill, RULE_STYLES } from "@/lib/retention";
import { RuleDialog } from "@/components/retention/RuleDialog";

export const RuleLibrary = ({ rules, meta, canEdit, onChanged }) => {
  const [edit, setEdit] = useState(null);
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(apiError(e, "Action failed")); } };
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid="rule-table">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
          {["Rule", "Activity", "System / storage", "Trigger", "Period", "Owner", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-semibold">{h}</th>)}
        </tr></thead>
        <tbody>
          {rules.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-xs text-slate-500">No rules yet.</td></tr>}
          {rules.map((r) => (
            <tr key={r.id} className="border-b border-slate-50" data-testid={`rule-row-${r.id}`}>
              <td className="px-4 py-3"><div className="font-medium text-[#003366]">{r.name}</div><div className="text-[11px] font-mono text-slate-500">{r.rule_id} · {r.method_label}</div></td>
              <td className="px-4 py-3 text-xs text-slate-700">{r.activity_name}</td>
              <td className="px-4 py-3 text-xs text-slate-700">{r.system_name}<div className="text-[11px] text-slate-500">{r.storage_name}</div></td>
              <td className="px-4 py-3 text-xs">{r.trigger_label}</td>
              <td className="px-4 py-3 text-xs font-mono">{r.retention_months}m<div className="text-[10px] text-slate-400 font-sans max-w-[160px] truncate">{r.legal_basis}</div></td>
              <td className="px-4 py-3 text-xs">{r.owner}</td>
              <td className="px-4 py-3"><Pill cls={RULE_STYLES[r.status]} testId={`rule-status-${r.id}`}>{r.status_label}</Pill></td>
              <td className="px-4 py-3">
                {canEdit && (
                  <div className="flex gap-1 justify-end">
                    {["DRAFT", "ACTIVE"].includes(r.status) && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setEdit(r)} data-testid={`rule-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>}
                    {r.status === "DRAFT" && <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`rule-submit-${r.id}`}
                      onClick={() => act(() => api.post(`/retention/rules/${r.id}/submit`, {}), "Sent for approval")}><Send className="w-3 h-3 mr-1" /> Submit</Button>}
                    {r.status !== "RETIRED" && r.status !== "PENDING_APPROVAL" && <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" data-testid={`rule-retire-${r.id}`}
                      onClick={() => { const c = window.prompt("Reason for retiring this rule"); if (c) act(() => api.post(`/retention/rules/${r.id}/retire`, { comment: c }), "Rule retired"); }}>
                      <Archive className="w-3.5 h-3.5" /></Button>}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <RuleDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} meta={meta} rule={edit} onSaved={onChanged} />
    </div>
  );
};
